'use client';

import type { ProductDetailCollectEvent } from 'src/actions/products/product-bff';

import { useState } from 'react';

import { Alert, Stack, Button, Typography, CircularProgress } from '@mui/material';

import { streamProductDetailCollect } from 'src/actions/products/product-bff';

import { Iconify } from 'src/components/iconify';

import { COLLECT_STATUS_LABEL } from '../constants/market-rankings-labels';
import { mallProductId, mallProductLink } from '../utils/market-rankings-product';

import type { MarketProduct } from '../utils/market-rankings-product';

type CollectState = {
  running: boolean;
  error: string | null;
  event: ProductDetailCollectEvent | null;
  result: (ProductDetailCollectEvent & { detailMediaCount?: number }) | null;
};

const IDLE: CollectState = { running: false, error: null, event: null, result: null };

/**
 * 상세페이지 수집을 **직접 거는** 바.
 *
 * 랭킹 밖 상품(판매몰 검색분)과, 랭킹에 있어도 상위 N개 밖이라 자동 수집이 안 된 상품이
 * 함께 쓴다.
 *
 * ⚠️ **몰 ID와 링크가 둘 다 있어야** 수집할 수 있다. 화해는 랭킹 문서의 링크 형식이 달라
 * `mallProductLink`가 다시 만든다.
 *
 * ⚠️ `done` 이벤트만 결과이고 나머지는 진행 상황이다 — 둘을 같은 자리에 담으면
 * 마지막 진행 문구가 결과를 덮는다.
 */
export default function DetailCollectBar({
  product,
  hasCollected,
  onCollected,
  note,
}: {
  product: MarketProduct;
  hasCollected?: boolean;
  onCollected?: () => void | Promise<void>;
  /** 왜 수집이 필요한지 — 안 밝히면 버튼이 왜 있는지 알 수 없다 */
  note?: string;
}) {
  const [collect, setCollect] = useState<CollectState>(IDLE);

  const id = mallProductId(product);
  const link = mallProductLink(product);
  const canCollect = Boolean(id && link);

  const runCollect = async () => {
    setCollect({ running: true, error: null, event: null, result: null });
    try {
      await streamProductDetailCollect(
        [{ source: product.source, id, link, title: product.title, brand: product.brand }],
        (event) => {
          if (event?.status === 'done') {
            const results = (event.results as CollectState['result'][]) || [];
            setCollect((prev) => ({ ...prev, result: results[0] || null }));
          } else {
            setCollect((prev) => ({ ...prev, event }));
          }
        },
      );
      setCollect((prev) => ({ ...prev, running: false }));
      await onCollected?.();
    } catch (caught) {
      const failure = caught as { message?: unknown };
      const message = typeof failure?.message === 'string' ? failure.message : '';
      setCollect({ running: false, error: message || '상세 수집에 실패했습니다.', event: null, result: null });
    }
  };

  const status = collect.result?.status || collect.event?.status;

  return (
    <Stack spacing={1}>
      {note && !hasCollected ? (
        <Alert severity="info" variant="outlined" icon={<Iconify icon="solar:info-circle-linear" width={18} />}>
          <Typography sx={{ typography: 'uiMeta', lineHeight: 1.5 }}>{note}</Typography>
        </Alert>
      ) : null}

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Button
          size="small"
          variant={hasCollected ? 'outlined' : 'contained'}
          disabled={collect.running || !canCollect}
          onClick={runCollect}
          startIcon={
            collect.running ? (
              <CircularProgress size={14} thickness={6} color="inherit" />
            ) : (
              <Iconify icon="solar:magic-stick-3-linear" width={16} />
            )
          }
        >
          {collect.running ? '상세 수집 중…' : hasCollected ? '상세 다시 수집' : '상세페이지 수집·분석'}
        </Button>
        {product.link ? (
          <Button
            size="small"
            variant="text"
            href={product.link}
            target="_blank"
            rel="noopener"
            endIcon={<Iconify icon="solar:square-top-down-linear" width={14} />}
          >
            판매처에서 보기
          </Button>
        ) : null}
        {status ? (
          <Typography variant="caption" sx={{ color: status === 'error' ? 'error.main' : 'text.secondary' }}>
            {COLLECT_STATUS_LABEL[status] || status}
            {collect.result?.detailMediaCount ? ` · ${collect.result.detailMediaCount}컷` : ''}
          </Typography>
        ) : null}
        {!canCollect ? (
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            상품 링크가 없어 수집할 수 없습니다
          </Typography>
        ) : null}
      </Stack>

      {collect.error ? <Alert severity="error" variant="outlined">{collect.error}</Alert> : null}
      {status === 'nomedia' ? (
        <Alert severity="warning" variant="outlined">
          이 상품은 상세페이지에 이미지가 없어 수집할 내용이 없습니다. 기본정보만 사용할 수 있습니다.
        </Alert>
      ) : null}
    </Stack>
  );
}
